/* eslint-disable no-underscore-dangle */
import {sortBy} from '../components/common/utils/array';
import {error, success} from '../helpers/notifications';

const PlatformSdk = require('@gluenetworks/platform-sdk-remote');

const ControlUserEntity = PlatformSdk.entities.ws.ControlUser;
const UserTaskEntity = PlatformSdk.entities.ws.UserTask;

const Steve = require('@gluenetworks/steve_core').getInstance();

const CURRENT_ORGANIZATION_LABEL = 'Current Organization';

class UserService {
  getLoggedUser() {
    return window.DashboardComponent._userSession._userEntity;
  }

  getCurrentOrganizationId() {
    // eslint-disable-next-line no-underscore-dangle
    return window.DashboardComponent._userSession._activeOrganization.id;
  }

  getAuthorizedTenants() {
    return window.DashboardComponent._userSession._userEntity.getAuthorizedTenants();
  }

  getUserOrganizationsIds() {
    const loggedUser = this.getLoggedUser();
    return Object.keys(loggedUser.getAuthorizedTenants());
  }

  getLastSolutionForUser(user) {
    return user.getLastSolution(user._pristineProperties.lastRoute);
  }

  getUserRoleLabel(user) {
    return user._pristineProperties.actionGroupName;
  }

  getUserOrganizationsOptions() {
    const loggedUser = this.getLoggedUser();
    const organizationsMap = loggedUser.getAuthorizedTenants();
    const organizationsArray = [];
    Object.entries(organizationsMap).forEach((org) =>
      organizationsArray.push({
        id: org[0],
        label: org[1].name,
        value: org[1].name,
      })
    );
    organizationsArray.sort((orgA, orgB) => {
      return orgA.label.toLowerCase() < orgB.label.toLowerCase() ? -1 : 1;
    });
    return [
      {
        id: CURRENT_ORGANIZATION_LABEL,
        label: CURRENT_ORGANIZATION_LABEL,
        value: CURRENT_ORGANIZATION_LABEL,
      },
      ...organizationsArray,
    ];
  }

  async getUsersFeed(filterData = {}) {
    const mongoFilterObject = {
      lastLoggedIn: {
        $exists: true,
      },
    };
    if (filterData.organizations && filterData.organizations.length) {
      const orgIds = filterData.organizations.map(({id}) =>
        id === CURRENT_ORGANIZATION_LABEL ? this.getCurrentOrganizationId() : id
      );
      mongoFilterObject.tid = {
        $in: orgIds,
      };
    }
    if (filterData.roles && filterData.roles.length) {
      const rolesIds = filterData.roles.map(({id}) => id);
      mongoFilterObject.actionGroupId = {
        $in: rolesIds,
      };
    }
    const feed = await ControlUserEntity.startControlUsersFeed(
      '__authorizedTenants__',
      mongoFilterObject
    );
    const usersData = await feed.whenInitialized();
    return usersData;
  }

  async getDashboardOptionsForUser() {
    try {
      const user = Steve.controlAppNav._controluserInstance;
      const enableFavoriteHotkeys = user && user.getEnableFavoriteHotkeys();
      const enableFavoriteShortcuts = user && user.getEnableFavoriteShortcuts();
      const delay = user ? user.getFavoriteRotationDelay() * 1000 : 0;
      const theme = user && user.getTheme() ? user.getTheme() : 'LIGHT';
      const openSolutionsInNewTab = user && user.getOpenSolutionsInNewTab();
      return {
        enableFavoriteHotkeys,
        enableFavoriteShortcuts,
        delay,
        theme,
        openSolutionsInNewTab,
      };
    } catch (e) {
      error({message: e.errorMsg});
      return false;
    }
  }

  async getTheme() {
    try {
      const user = Steve.controlAppNav._controluserInstance;
      return user && user.getTheme() ? user.getTheme() : 'LIGHT';
    } catch (e) {
      error({message: e.errorMsg});
      return false;
    }
  }

  async setTheme(isLight) {
    try {
      const user = Steve.controlAppNav._controluserInstance;
      if (user) {
        user.setTheme(isLight ? 'LIGHT' : 'DARK');
        user.update();
      }
      return true;
    } catch (e) {
      error({message: e.errorMsg});
      return false;
    }
  }

  // We should use these methods to call services but we need to refator them first
  async updateTask({targetDate, name, description, priority, status, assignedTo}, selectedTask) {
    const taskId = selectedTask.getId();
    // const task = await UserTaskEntity.loadUserTask('__authorizedTenants__', taskId);
    const task = await this.getSelectedTaskEntity(selectedTask);
    task.setTargetDate(targetDate);
    task
      .setName(name)
      .setDescription(description)
      .setPriority(priority.value)
      .setStatus(status.value)
      .setTargetDate(targetDate);

    if (task._pristineProperties.assignedUserId !== assignedTo.value) {
      const newAssignee = await ControlUserEntity.loadControlUsers('__authorizedTenants__', {
        id: assignedTo.value,
      });
      newAssignee[0].addToAssignedTasksRelationship(taskId);
      await newAssignee[0].update();
    }
    await task.update();

    return task;
  }

  async getSelectedTaskEntity(selectedTask) {
    const taskId = selectedTask.getId();
    const userId = this.getLoggedUser().getId();
    const loggedControlUser = Steve.controlAppNav._controluserInstance;

    const mongoFilterObject = {
      id: {
        $eq: taskId,
      },
    };

    try {
      if (selectedTask._pristineProperties.createdUserId === userId) {
        const tasks = await loggedControlUser.loadRelatedCreatedTasks(mongoFilterObject);
        return tasks[0];
      }

      const tasks = await loggedControlUser.loadRelatedAssignedTasks(mongoFilterObject);
      return tasks[0];
    } catch (e) {
      error({message: e.errorMsg});
      return false;
    }
  }

  async removeTask(selectedTask) {
    const task = await this.getSelectedTaskEntity(selectedTask);

    try {
      await task.removeControlUserRelationships(
        selectedTask._pristineProperties.createdUserId,
        selectedTask._pristineProperties.assignedUserId
      );
      await task.delete();
    } catch (e) {
      return error({
        message: `Error deleting task: Something went wrong, please try again later.`,
      });
    }

    success({name: task.getName(), message: ' task successfully deleted.'});
  }

  async createNewTask({name, description, status, priority, assignedTo, targetDate}) {
    try {
      const organizationId = this.getCurrentOrganizationId();
      const creator = Steve.controlAppNav._controluserInstance;
      const task = new UserTaskEntity(organizationId);
      task
        .setName(name)
        .setDescription(description)
        .setPriority(priority.value)
        .setStatus(status.value)
        .addControlUserRelationships(creator.getId(), assignedTo.value);
      if (targetDate) {
        task.setTargetDate(targetDate.getTime());
      }
      await task.create();

      const taskId = task && task.getId();
      if (taskId) {
        success({
          name: task.getName(),
          message: ' task successfully created. You can find it in the list of tasks.',
        });
        return task.getId();
      }
      return null;
    } catch (e) {
      error({message: e.errorMsg});
      return null;
    }
  }

  async getAssignedToMeTaskListFeed(filterData = {}) {
    const mongoFilterObject = {};
    if (filterData.assignedTo.value !== undefined && filterData.assignedTo.value.length > 0) {
      mongoFilterObject.assignedUserId = {
        $eq: filterData.assignedTo.value,
      };
    }
    if (filterData.creator.value !== undefined && filterData.creator.value.length > 0) {
      mongoFilterObject.createdUserId = {
        $eq: filterData.creator.value,
      };
    }
    if (filterData.priority && filterData.priority.length) {
      const priority = filterData.priority.map(({value}) => value);
      mongoFilterObject.priority = {
        $in: priority,
      };
    }
    if (filterData.status && filterData.status.length) {
      const status = filterData.status.map(({value}) => value);
      mongoFilterObject.status = {
        $in: status,
      };
    }
    const organizationId = this.getCurrentOrganizationId();
    const userId = this.getLoggedUser().getId();
    const loggedControlUser = await ControlUserEntity.retrieveAscendControlUser(
      organizationId,
      userId
    );
    const assignedFeed = await loggedControlUser.startRelatedAssignedTasksFeed(mongoFilterObject);
    const assignedTasks = await assignedFeed.whenInitialized();
    return assignedTasks;
  }

  async getCreatedByMeTaskList(filterData = {}) {
    const mongoFilterObject = {};
    if (filterData.assignedTo.value !== undefined && filterData.assignedTo.value.length > 0) {
      mongoFilterObject.assignedUserId = {
        $eq: filterData.assignedTo.value,
      };
    }
    if (filterData.creator.value !== undefined && filterData.creator.value.length > 0) {
      mongoFilterObject.createdUserId = {
        $eq: filterData.creator.value,
      };
    }
    if (filterData.priority && filterData.priority.length) {
      const priority = filterData.priority.map(({value}) => value);
      mongoFilterObject.priority = {
        $in: priority,
      };
    }
    if (filterData.status && filterData.status.length) {
      const status = filterData.status.map(({value}) => value);
      mongoFilterObject.status = {
        $in: status,
      };
    }
    const organizationId = this.getCurrentOrganizationId();
    const userId = this.getLoggedUser().getId();
    const loggedControlUser = await ControlUserEntity.retrieveAscendControlUser(
      organizationId,
      userId
    );
    const createdFeed = await loggedControlUser.startRelatedCreatedTasksFeed(mongoFilterObject);
    const createdTasks = await createdFeed.whenInitialized();
    return createdTasks;
  }

  async getUsersForCurrentOrganization() {
    const mongoQurey = {
      $and: [
        {
          firstName: {
            $exists: true,
          },
        },
        {
          lastName: {
            $exists: true,
          },
        },
      ],
    };
    const usersData = await ControlUserEntity.loadControlUsers('__authorizedTenants__', mongoQurey);
    const users = usersData
      .filter((user) => Object.keys(user._pristineProperties).length > 0)
      .map((user) => {
        const {firstName, lastName, username} = user._pristineProperties;
        const usernameInLabel = username ? ` (${username})` : '';
        const label = `${firstName} ${lastName}${usernameInLabel}`;
        const id = user.getId();
        return {
          label,
          value: id,
        };
      });
    const sortedUsers = users.sort(sortBy('label'));
    return sortedUsers;
  }
}

export default new UserService();
